import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Linkedin, Twitter, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { interviewContent, ambassadorPortrait, linkedinCompanyUrl, interviewGallery } from '../data/interview';
import Header from '../components/Header';
import Footer from '../components/Footer';

const InterviewPage = () => {
  const { language } = useLanguage();
  const i = interviewContent[language] || interviewContent.fr;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const previousTitle = document.title;
    document.title = `${i.title} ${i.ambassador} – Business Club France–Latvia`;
    return () => {
      document.title = previousTitle;
    };
  }, [i.title, i.ambassador]);

  return (
    <div
      className="min-h-screen bg-[#1a1c1b] text-white"
      data-testid="interview-page"
    >
      <Header />

      <main className="pt-32 md:pt-36 pb-24">
        {/* Hero of the page */}
        <section className="max-w-[87.5rem] mx-auto px-6 md:px-10 mb-16">
          <Link
            to="/"
            data-testid="interview-back-link"
            className="group inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm uppercase tracking-wider mb-10"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-1 transition-transform"
            />
            {i.ctaBack}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="relative">
                <div className="absolute -inset-2 border border-[#d9fb06]/20 rounded-sm" />
                <div className="relative overflow-hidden rounded-sm aspect-[4/5] bg-[#0f1110]">
                  <img
                    src={ambassadorPortrait}
                    alt={`${i.ambassador} - ${i.role}`}
                    className="w-full h-full object-cover grayscale-[15%]"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-[#d9fb06]" />
                <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
                  {i.eyebrow}
                </span>
              </div>

              <h1 className="font-black text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.92] mb-6">
                {i.title}
                <span className="block text-[#d9fb06] mt-3">{i.ambassador}</span>
              </h1>

              <p className="text-[#888680] text-base md:text-lg mb-2">{i.role}</p>
              <p className="text-[#c9c7c0] text-sm md:text-base italic max-w-2xl">
                {i.pageTitle}
              </p>
            </div>
          </div>
        </section>

        {/* Q&A */}
        <section className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="space-y-14 md:space-y-16">
            {i.questions.map((item, idx) => (
              <article
                key={idx}
                data-testid={`interview-question-${idx + 1}`}
                className="relative"
              >
                <div className="flex items-start gap-5 mb-5">
                  <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#d9fb06] text-[#1a1c1b] flex items-center justify-center font-black text-lg md:text-xl">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <h2 className="flex-1 text-white font-bold text-xl md:text-2xl lg:text-[1.625rem] leading-snug pt-1">
                    {item.q}
                  </h2>
                </div>

                <div className="pl-0 md:pl-[4.75rem] space-y-4">
                  {item.a.map((paragraph, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-[#c9c7c0] text-base md:text-lg leading-[1.75]"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>

                {idx < i.questions.length - 1 && (
                  <div className="mt-14 md:mt-16 flex justify-center">
                    <div className="h-px w-24 bg-[#3f4816]" />
                  </div>
                )}

                {/* Gallery photo if any after this question */}
                {(() => {
                  const photo = interviewGallery.find((g) => g.afterQuestion === idx);
                  if (!photo) return null;
                  return (
                    <figure
                      data-testid={`interview-photo-${idx + 1}`}
                      className="mt-12 md:mt-16 -mx-2 sm:mx-0"
                    >
                      <div className="relative overflow-hidden rounded-sm border border-[#3f4816]/60 bg-[#0f1110]">
                        <img
                          src={photo.src}
                          alt={photo.caption[language] || photo.caption.fr}
                          loading="lazy"
                          className="w-full h-auto object-cover aspect-[16/10] md:aspect-[16/9]"
                        />
                      </div>
                      <figcaption className="mt-4 flex items-start gap-3 text-[#888680] text-sm md:text-[0.95rem] italic leading-relaxed">
                        <span className="mt-1.5 inline-block h-px w-6 bg-[#d9fb06] flex-shrink-0" />
                        <span>{photo.caption[language] || photo.caption.fr}</span>
                      </figcaption>
                    </figure>
                  );
                })()}
              </article>
            ))}
          </div>

          {/* Share block */}
          <div
            className="mt-20 border-t border-[#3f4816]/60 pt-12"
            data-testid="interview-share-block"
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-3">
                <div className="h-px w-8 bg-[#d9fb06]" />
                <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">
                  {i.share.heading}
                </span>
                <div className="h-px w-8 bg-[#d9fb06]" />
              </div>
              <p className="text-[#888680] text-sm md:text-base max-w-xl mx-auto">
                {i.share.subheading}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  'https://bcfl.eu/interview-ambassadeur'
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin-btn"
                className="group inline-flex items-center gap-2.5 bg-[#0a66c2] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#0a66c2]/85 hover:scale-[1.02] transition-all duration-300"
              >
                <Linkedin size={18} fill="currentColor" strokeWidth={0} />
                {i.share.linkedin}
              </a>

              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  i.share.shareText
                )}&url=${encodeURIComponent('https://bcfl.eu/interview-ambassadeur')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-twitter-btn"
                className="group inline-flex items-center gap-2.5 bg-white text-black px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#d9fb06] hover:scale-[1.02] transition-all duration-300"
              >
                <Twitter size={18} fill="currentColor" strokeWidth={0} />
                {i.share.twitter}
              </a>
            </div>

            <div className="flex justify-center">
              <a
                href={linkedinCompanyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="follow-linkedin-btn"
                className="group inline-flex items-center gap-2 text-[#d9fb06] text-sm font-medium hover:opacity-80 transition-opacity border-b border-dashed border-[#d9fb06]/40 hover:border-[#d9fb06] pb-1"
              >
                <Linkedin size={16} fill="currentColor" strokeWidth={0} />
                {i.share.follow}
                <ExternalLink
                  size={13}
                  className="group-hover:translate-x-0.5 -translate-y-0.5 transition-transform"
                />
              </a>
            </div>
          </div>

          {/* Back CTA */}
          <div className="mt-16 flex justify-center">
            <Link
              to="/"
              data-testid="interview-back-bottom"
              className="group inline-flex items-center gap-3 border border-[#d9fb06] text-[#d9fb06] px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all duration-300"
            >
              <ArrowLeft
                size={18}
                className="group-hover:-translate-x-1 transition-transform"
              />
              {i.ctaBack}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InterviewPage;
